import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, RefreshCw, FileText, EyeOff } from 'lucide-react';
import TopNav from '../components/sections/TopNav';
import SiteFooter from '../components/sections/SiteFooter';
import SubmitTipButton from '../components/SubmitTipButton';
import ShareButtons from '../components/ShareButtons';
import Redacted from '../components/Redacted';
import { useJsonLd, buildBreadcrumb } from '../utils/jsonLd';
import PageSeoTag from '../components/PageSeoTag';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE_URL = (() => {
  try { return `${window.location.origin}/wall`; }
  catch { return 'https://www.outlawmedia.com.au/the-rumour-file'; }
})();

// Deterministic pseudo-random per tip id, so the "rotation/tint" doesn't
// change between renders on the same card
const hashId = (id) => {
  const s = String(id || '');
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
};

// Slightly aged paper tints for variety
const PAPER_TINTS = ['#f4ecd8', '#f7ecc9', '#efe4c9', '#f3ead2', '#ede1c1', '#f0e2b8'];
const TAPE_COLORS = ['rgba(250, 240, 137, 0.85)', 'rgba(255, 210, 170, 0.85)', 'rgba(250, 220, 200, 0.85)'];

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric',
    timeZone: 'Australia/Melbourne',
  });
};

const RumourPaper = ({ tip, index }) => {
  const h = hashId(tip.id);
  const rotate = ((h % 700) / 100 - 3.5).toFixed(2); // -3.5° to +3.5°
  const tint = PAPER_TINTS[h % PAPER_TINTS.length];
  const tape = TAPE_COLORS[h % TAPE_COLORS.length];
  const tapeTilt = ((h % 400) / 100 - 2).toFixed(2); // -2° to +2°
  return (
    <article
      data-testid={`rumour-paper-${tip.id}`}
      className="rumour-paper"
      style={{
        '--paper-tint': tint,
        '--paper-rotate': `${rotate}deg`,
        '--tape-color': tape,
        '--tape-rotate': `${tapeTilt}deg`,
        animationDelay: `${(index % 12) * 60}ms`,
      }}
    >
      <span className="rumour-tape" aria-hidden="true" />
      <div className="rumour-meta">
        <span className="rumour-category">{tip.category || 'Rumour'}</span>
        <span className="rumour-date">{formatDate(tip.published_at)}</span>
      </div>
      <p className="rumour-body"><Redacted text={tip.redacted_text} /></p>
      <p className="rumour-sig">- filed anonymously to Outlaw Media</p>
    </article>
  );
};

const RumourFile = () => {
  const [tips, setTips] = useState([]);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useJsonLd(buildBreadcrumb([
    { name: 'Home', url: 'https://www.outlawmedia.com.au/' },
    { name: 'The Rumour File', url: PAGE_URL },
  ]), 'wall-breadcrumb');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    setLocked(false);
    try {
      const res = await fetch(`${API}/api/tips/wall`, { cache: 'no-store', credentials: 'include' });
      if (!res.ok) throw new Error('Could not load rumours.');
      const data = await res.json();
      if (data && data.locked) {
        setLocked(true);
        setTips([]);
      } else {
        setTips(Array.isArray(data) ? data : []);
      }
    } catch (e) {
      setError(e.message || 'Could not load rumours.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const list = useMemo(() => tips || [], [tips]);

  return (
    <div className="min-h-screen bg-black text-white" data-testid="rumour-file-page">
      <TopNav />
      <PageSeoTag pageKey="wall" overrides={{
        title: 'The Rumour File | Outlaw Media - Ryan Naumenko',
        description: 'Anonymous rumours filed to Outlaw Media by the public. SOME names and locations are manually redacted for security purposes. In memory of Derryn Hinch (1944-2026).',
      }} />

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Caveat:wght@500;700&family=Special+Elite&display=swap');

        .rumour-corkboard {
          background:
            radial-gradient(ellipse at 25% 15%, rgba(139, 69, 19, 0.35) 0%, transparent 45%),
            radial-gradient(ellipse at 80% 90%, rgba(80, 40, 15, 0.4) 0%, transparent 50%),
            repeating-radial-gradient(circle at 30% 20%, #6b4423 0px, #5a3919 2px, #6b4423 4px),
            #5a3919;
          background-blend-mode: multiply, multiply, overlay, normal;
          position: relative;
        }
        .rumour-corkboard::before {
          content: "";
          position: absolute;
          inset: 0;
          background-image:
            radial-gradient(circle at 12% 22%, rgba(0,0,0,0.35) 0.5px, transparent 1px),
            radial-gradient(circle at 88% 45%, rgba(0,0,0,0.3) 0.5px, transparent 1px),
            radial-gradient(circle at 45% 78%, rgba(0,0,0,0.28) 0.5px, transparent 1px),
            radial-gradient(circle at 65% 15%, rgba(0,0,0,0.32) 0.5px, transparent 1px);
          background-size: 40px 40px, 55px 55px, 70px 70px, 30px 30px;
          opacity: 0.9;
          pointer-events: none;
        }

        @keyframes paperIn {
          0% { opacity: 0; transform: translateY(12px) rotate(var(--paper-rotate)) scale(0.96); }
          100% { opacity: 1; transform: translateY(0) rotate(var(--paper-rotate)) scale(1); }
        }
        .rumour-paper {
          position: relative;
          background: var(--paper-tint);
          color: #2b2418;
          padding: 2.25rem 1.5rem 1.5rem;
          border-radius: 3px;
          box-shadow:
            0 1px 0 rgba(0,0,0,0.05) inset,
            0 8px 22px -6px rgba(0, 0, 0, 0.55),
            0 22px 40px -18px rgba(0, 0, 0, 0.7);
          transform: rotate(var(--paper-rotate));
          transform-origin: center top;
          transition: transform 260ms ease, box-shadow 260ms ease;
          background-image:
            linear-gradient(180deg, rgba(255,255,255,0.35) 0%, transparent 12%),
            linear-gradient(0deg, rgba(0,0,0,0.05) 0%, transparent 20%),
            repeating-linear-gradient(0deg, rgba(150, 120, 60, 0.06) 0px, transparent 2px, transparent 26px, rgba(150, 120, 60, 0.06) 27px);
          animation: paperIn 500ms ease-out both;
          break-inside: avoid;
        }
        .rumour-paper:hover {
          transform: rotate(calc(var(--paper-rotate) * 0.4)) translateY(-3px);
          box-shadow: 0 14px 30px -8px rgba(0,0,0,0.7), 0 30px 55px -22px rgba(0,0,0,0.75);
        }

        /* Washi tape at top */
        .rumour-tape {
          position: absolute;
          top: -14px;
          left: 50%;
          transform: translateX(-50%) rotate(var(--tape-rotate));
          width: 88px;
          height: 26px;
          background: var(--tape-color);
          box-shadow: 0 2px 6px rgba(0,0,0,0.35);
          border-radius: 1px;
          background-image:
            repeating-linear-gradient(90deg, rgba(255,255,255,0.25) 0 4px, transparent 4px 12px);
          clip-path: polygon(4% 0, 100% 0, 96% 100%, 0 100%);
        }

        .rumour-meta {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.5rem;
          font-family: 'Special Elite', monospace;
          font-size: 0.7rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: #6b5a35;
          border-bottom: 1px dashed rgba(80, 60, 20, 0.35);
          padding-bottom: 0.55rem;
          margin-bottom: 0.85rem;
        }
        .rumour-category { font-weight: 700; color: #a13b1b; }
        .rumour-date { opacity: 0.85; }

        .rumour-body {
          font-family: 'Caveat', cursive;
          font-weight: 500;
          font-size: 1.35rem;
          line-height: 1.35;
          color: #2b2418;
          white-space: pre-wrap;
          word-break: break-word;
        }
        .rumour-sig {
          font-family: 'Special Elite', monospace;
          font-size: 0.65rem;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: #6b5a35;
          margin-top: 1rem;
          text-align: right;
          opacity: 0.85;
        }

        .rumour-masonry {
          column-count: 1;
          column-gap: 1.25rem;
          /* On mobile the single column would otherwise span edge-to-edge
             (~365px on a 390px viewport) making the paper cards feel less
             like pinned notes and more like a full-width feed. Cap the
             single-column width so cards sit with breathing room. */
          max-width: 20rem;
          margin-left: auto;
          margin-right: auto;
        }
        @media (min-width: 480px) {
          .rumour-masonry { max-width: 22rem; }
        }
        @media (min-width: 640px) {
          .rumour-masonry { column-count: 2; column-gap: 1.75rem; max-width: none; }
        }
        @media (min-width: 1024px) {
          .rumour-masonry { column-count: 3; column-gap: 2rem; }
        }
        .rumour-masonry > article { margin-bottom: 2rem; display: inline-block; width: 100%; }

        .rumour-hinch-plaque {
          background: rgba(0,0,0,0.55);
          border: 1px solid rgba(220, 38, 38, 0.5);
          border-radius: 8px;
          padding: 0.85rem 1rem;
          font-family: 'Special Elite', monospace;
          line-height: 1.4;
        }
      `}</style>

      {/* Hero header - dark, dignified, tribute-forward */}
      <header className="relative pt-8 pb-10 px-4 border-b border-zinc-800">
        <div className="max-w-4xl mx-auto">
          <Link
            to="/"
            data-testid="rumour-back-link"
            className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-gray-400 hover:text-red-400 transition-colors mb-4"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Back to Home
          </Link>

          <div className="flex items-end justify-between gap-3 flex-wrap">
            <div>
              <p className="text-[11px] font-bold uppercase tracking-wider text-red-400 mb-1">
                Anonymous Rumours
              </p>
              <h1 className="text-3xl md:text-4xl font-black tracking-tight leading-tight">
                The <span className="text-red-500">Rumour File</span>
              </h1>
              <p className="mt-3 text-sm text-gray-400 max-w-2xl">
                Rumours filed anonymously by the public. Some names and locations
                are manually redacted for security - everything else is published as
                submitted, pinned to the board.
              </p>
            </div>
            <ShareButtons
              url={PAGE_URL}
              title="The Rumour File - Outlaw Media"
              text="Anonymous rumours from the public, pinned to the board. Read The Rumour File."
              variant="pill"
              testId="rumour-share"
            />
          </div>

          {/* Hinch tribute plaque */}
          <div className="rumour-hinch-plaque mt-6 text-[13px] md:text-sm text-gray-200" data-testid="hinch-tribute">
            <p className="text-red-400 uppercase text-[10px] mb-1 font-bold tracking-wider">In Memoriam</p>
            <p>
              This wall is dedicated to <strong className="text-white">Derryn Hinch</strong> (1944 – 2026),
              media legend and creator of <em>The Rumour File</em>. He taught a generation of journalists to
              name names and shame the shameless. He was my inspiration coming up in this game.
            </p>
            <p className="text-gray-400 mt-1">- Ryan Naumenko, Outlaw Media</p>
          </div>

          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <Link
              to="/tip"
              data-testid="rumour-submit-cta"
              className="inline-flex items-center gap-2 px-3 py-2 rounded bg-red-600 hover:bg-red-500 text-white font-bold text-xs uppercase tracking-wider transition-colors"
            >
              <EyeOff className="h-3.5 w-3.5" />
              File a Rumour
            </Link>
            <button
              type="button"
              onClick={load}
              data-testid="rumour-refresh"
              className="inline-flex items-center gap-2 px-3 py-2 rounded bg-zinc-900/80 hover:bg-zinc-800 border border-zinc-800 text-xs font-bold uppercase tracking-wider"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>
      </header>

      {/* Corkboard */}
      <main className="rumour-corkboard px-4 sm:px-6 lg:px-8 py-10 md:py-14">
        <div className="relative max-w-6xl mx-auto">
          {error && (
            <div className="bg-red-950/60 border border-red-500/50 text-red-200 px-3 py-2 rounded text-sm mb-6 max-w-2xl mx-auto">
              {error}
            </div>
          )}

          {loading && list.length === 0 && (
            <div className="text-center text-yellow-100/70 py-12 font-mono uppercase tracking-widest text-sm" data-testid="rumour-loading">
              Loading rumours…
            </div>
          )}

          {!loading && locked && (
            <div className="max-w-md mx-auto text-center rounded-lg border-2 border-red-600/60 bg-black/70 px-6 py-10" data-testid="rumour-locked">
              <p className="text-red-500 text-[11px] font-black uppercase tracking-[0.3em] mb-3">Reader Account Required</p>
              <h2 className="text-2xl font-black text-white mb-3" style={{ fontFamily: 'Georgia, serif' }}>
                Subscribe to read the Rumour File.
              </h2>
              <p className="text-zinc-300 text-sm mb-2">
                Reports are for subscribers only. One-tap sign-up, no password. Please choose your unlimited subscription plan next.
              </p>
              <p className="text-yellow-300/90 text-xs mb-5">
                Monthly $13, Annual $130, or Founding $249 one-off.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  to="/tip"
                  data-testid="locked-file-rumour"
                  className="inline-flex items-center justify-center px-4 py-2 rounded bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white font-bold text-xs uppercase tracking-widest"
                >
                  File a rumour
                </Link>
                <Link
                  to="/login"
                  data-testid="locked-subscribe"
                  className="inline-flex items-center justify-center px-4 py-2 rounded bg-red-600 hover:bg-red-500 text-white font-black text-xs uppercase tracking-widest"
                >
                  Sign up · Subscribe
                </Link>
              </div>
            </div>
          )}

          {!loading && !locked && list.length === 0 && (
            <div className="text-center py-16 bg-black/40 rounded-lg border border-yellow-900/30 max-w-lg mx-auto" data-testid="rumour-empty">
              <FileText className="h-10 w-10 text-yellow-200/70 mx-auto mb-4" />
              <p className="text-yellow-100/80 text-sm font-mono uppercase tracking-widest">The board is empty for now.</p>
              <Link
                to="/tip"
                className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-500 text-white font-black text-xs uppercase tracking-widest transition-colors"
              >
                File the First Rumour
              </Link>
            </div>
          )}

          {list.length > 0 && (
            <div className="rumour-masonry" data-testid="rumour-masonry">
              {list.map((t, i) => <RumourPaper key={t.id} tip={t} index={i} />)}
            </div>
          )}
        </div>
      </main>

      <SiteFooter />
      <SubmitTipButton />
    </div>
  );
};

export default RumourFile;
